/**
 * MonthGrid — month view, interactive like the week board (minus door rows).
 *
 * 7-column calendar grid. Each day cell shows its appointments as compact
 * status-color chips (full time range + carrier). Interactions:
 *   - click a chip            → detail (edit from there)
 *   - right-click a chip      → context menu
 *   - drag a chip to a day    → reschedule to that date, SAME time + door
 *                               (there are no door rows here, so set door/time
 *                               by editing the appointment afterward)
 *   - "+N more"               → opens a floating popover over the days below
 *                               (highlighted, same-day) with the full list; the
 *                               cell itself does not resize
 *   - click empty space       → open the full day board for that date
 *
 * Drop target resolved on release via elementFromPoint. Grouping uses local
 * dates (parseISO of scheduledStartUtc), matching the day board and week board.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { format, parseISO, addDays, startOfMonth, startOfWeek } from 'date-fns';
import type { AppointmentDto, AppointmentStatusCode, AppointmentEventDto } from '@/api/types';
import type { MoveAppointmentEvent, RequestContextMenuEvent } from '@/components/GanttCalendar';
import { useAppointmentHover, LaborPulse, LaborPausedMark, LaborDoneMark, DriverBadge, PaperworkBadge, LicenceBadge } from '@/components/AppointmentHoverCard';
import { NoWmsBadge } from '@/components/WmsMarks';
import { loadSubLine } from '@/lib/loadIdentifier';
import { BOARD_HEADER_STRIP_CLASS } from '@/components/StatusFilterBar';
import { FRAME_BORDER } from '@/lib/utils';

type CapBand = {
    dayOfWeek: number;
    direction: string;
    startMinutes: number;
    endMinutes: number;
    maxConcurrent: number;
    effectiveStartDate?: string | null;
    effectiveEndDate?: string | null;
};
const bandAppliesOn = (b: CapBand, dateStr: string): boolean =>
    (!b.effectiveStartDate || dateStr >= b.effectiveStartDate) &&
    (!b.effectiveEndDate || dateStr <= b.effectiveEndDate);

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_CHIPS = 2;

const STATUS_CHIP: Record<AppointmentStatusCode, string> = {
    REQUESTED: 'bg-status-requested/85 text-white',
    APPROVED:  'bg-status-approved/85 text-white',
    ARRIVED:   'bg-status-arrived/85 text-white',
    DEPARTED:  'bg-status-departed/70 text-white',
    NOSHOW:    'bg-status-noshow/85 text-white',
    CANCELLED: 'bg-status-cancelled/40 text-white line-through',
};
const BLOCK_CHIP = 'bg-slate-500/80 text-white';

function chipClass(a: AppointmentDto): string {
    if (
        a.direction === 'BLOCK' &&
        a.statusCode !== 'CANCELLED' &&
        a.statusCode !== 'NOSHOW' &&
        a.statusCode !== 'DEPARTED'
    ) {
        return BLOCK_CHIP;
    }
    return STATUS_CHIP[a.statusCode];
}

const pad = (n: number) => String(n).padStart(2, '0');
function dowOf(ds: string): number {
    return new Date(`${ds}T00:00:00.000Z`).getUTCDay() + 1;
}
function fmtMin(m: number): string {
    const h24 = Math.floor(m / 60);
    const mm = m % 60;
    const ap = h24 < 12 ? 'a' : 'p';
    let hr = h24 % 12;
    if (hr === 0) hr = 12;
    return mm === 0 ? `${hr}${ap}` : `${hr}:${pad(mm)}${ap}`;
}
function dirShort(d: string): string {
    return d === 'INBOUND' ? 'IN' : d === 'OUTBOUND' ? 'OUT' : 'ALL';
}

// Same IN/OUT badge as the day board's Limits summary.
function DirBadge({ k }: { k: 'IN' | 'OUT' }) {
    return (
        <span
            className={`inline-block rounded-sm border px-1 text-[8px] font-semibold leading-tight ${
                k === 'IN'
                    ? 'bg-blue-100 text-blue-800 border-blue-300'
                    : 'bg-orange-100 text-orange-800 border-orange-300'
            }`}
        >
            {k}
        </span>
    );
}

// Direction tag on an appointment block — matches the day board (IN / OUT / BLK).
function DirTag({ dir }: { dir: string }) {
    const label = dir === 'BLOCK' ? 'BLK' : dir === 'INBOUND' ? 'IN' : 'OUT';
    const cls =
        dir === 'BLOCK'
            ? 'bg-slate-200 text-slate-700 border-slate-400'
            : dir === 'INBOUND'
              ? 'bg-blue-100 text-blue-800 border-blue-300'
              : 'bg-orange-100 text-orange-800 border-orange-300';
    return (
        <span className={`inline-block shrink-0 rounded border px-1 text-[8px] font-semibold leading-tight ${cls}`}>
            {label}
        </span>
    );
}

// Secondary line: PO (or consignee for outbound / doc number) + pallet count —
// identical logic to the day board's block.
// It was a COPY of the day board's block, in a file that could not see it, and
// all three copies had to be found and edited together to add the container
// (2026-08-27) — which is exactly how two of them would have been missed.
// See lib/loadIdentifier.ts for why the appointment number now comes last.
const subLine = loadSubLine;

export interface MonthGridProps {
    monthAnchor: string;   // yyyy-MM-dd — any day in the target month
    /** ⭐ The status strip's CONTENT. The frame is this component's job; see below. */
    header?: ReactNode;
    appointments: AppointmentDto[];
    capBands: CapBand[];
    onSelectAppointment: (a: AppointmentDto) => void;
    /**
     * ---- ⭐⭐ DOUBLE CLICK OPENS THE EDITOR (Pete, 2026-09-27) ----------------
     *
     * > *"Double-click on Week and Month view should pull the appointment into EDIT"*
     *
     * ⚠️ THE SINGLE CLICK STILL FIRES FIRST and still opens the detail dialog; the
     * PAGE closes it as it opens the editor. ⛔ Not debounced here — a timer would
     * lag every ordinary click to serve the rarer gesture.
     *
     * ⚠️ OPTIONAL, so a caller with no editor keeps today's behaviour.
     */
    onEditAppointment?: (a: AppointmentDto) => void;
    onMoveAppointment: (ev: MoveAppointmentEvent) => void;
    onRequestContextMenu: (ev: RequestContextMenuEvent) => void;
    onOpenDay: (date: string) => void;
    todayStr: string;
    fetchEvents?: (appointmentId: number) => Promise<AppointmentEventDto[]>;
}

interface DragState {
    appt: AppointmentDto;
    originX: number;
    originY: number;
    x: number;
    y: number;
    moved: boolean;
}

export function MonthGrid({
    monthAnchor,
    header,
    appointments,
    capBands,
    onSelectAppointment,
    onEditAppointment,
    onMoveAppointment,
    onRequestContextMenu,
    onOpenDay,
    todayStr,
    fetchEvents,
}: MonthGridProps) {
    const monthStart = startOfMonth(parseISO(monthAnchor));
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const monthNum = monthStart.getMonth();
    const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

    const byDay = useMemo(() => {
        const m = new Map<string, AppointmentDto[]>();
        for (const a of appointments) {
            const key = format(parseISO(a.scheduledStartUtc), 'yyyy-MM-dd');
            const arr = m.get(key);
            if (arr) arr.push(a);
            else m.set(key, [a]);
        }
        for (const list of m.values()) {
            list.sort(
                (a, b) =>
                    parseISO(a.scheduledStartUtc).getTime() - parseISO(b.scheduledStartUtc).getTime(),
            );
        }
        return m;
    }, [appointments]);

    const capByDow = useMemo(() => {
        const m = new Map<number, CapBand[]>();
        for (const b of capBands) {
            const arr = m.get(b.dayOfWeek);
            if (arr) arr.push(b);
            else m.set(b.dayOfWeek, [b]);
        }
        return m;
    }, [capBands]);

    // Which day's overflow popover is open, anchored to its cell rect.
    const [expanded, setExpanded] = useState<{ ds: string; rect: DOMRect } | null>(null);

    const [drag, setDrag] = useState<DragState | null>(null);
    const justDragged = useRef(false);
    const hover = useAppointmentHover(fetchEvents, drag?.moved ?? false);

    useEffect(() => {
        if (!drag) return;
        const onMove = (e: PointerEvent) => {
            setDrag((d) =>
                d
                    ? {
                          ...d,
                          x: e.clientX,
                          y: e.clientY,
                          moved: d.moved || Math.abs(e.clientX - d.originX) > 4 || Math.abs(e.clientY - d.originY) > 4,
                      }
                    : d,
            );
        };
        const onUp = (e: PointerEvent) => {
            const x = e.clientX;
            const y = e.clientY;
            setDrag((d) => {
                if (!d) return null;
                if (d.moved) {
                    justDragged.current = true;
                    const cell = (document.elementFromPoint(x, y) as HTMLElement | null)?.closest('[data-cell]');
                    const ds = cell?.getAttribute('data-date');
                    if (ds) {
                        const s = parseISO(d.appt.scheduledStartUtc);
                        const eDate = parseISO(d.appt.scheduledEndUtc);
                        const durMs = eDate.getTime() - s.getTime();
                        if (format(s, 'yyyy-MM-dd') !== ds) {
                            const startLocal = `${ds}T${pad(s.getHours())}:${pad(s.getMinutes())}:00`;
                            const ns = new Date(startLocal);
                            const ne = new Date(ns.getTime() + durMs);
                            onMoveAppointment({
                                appointmentId: d.appt.appointmentId,
                                dockDoorId: d.appt.dockDoorId, // unchanged — no door rows here
                                newStartUtc: ns.toISOString(),
                                newEndUtc: ne.toISOString(),
                            });
                        }
                    }
                }
                return null;
            });
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, [drag, onMoveAppointment]);

    // Close the overflow popover on Escape.
    useEffect(() => {
        if (!expanded) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpanded(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [expanded]);

    // Renders one appointment chip. In the cell it is draggable; in the popover
    // it is click/right-click/hover only (drag would drop onto the popover).
    const renderChip = (a: AppointmentDto, inPopover = false) => {
        const s = parseISO(a.scheduledStartUtc);
        const e = parseISO(a.scheduledEndUtc);
        const isDragging = drag?.appt.appointmentId === a.appointmentId && drag.moved;
        return (
            <div
                key={a.appointmentId}
                onPointerDown={
                    inPopover
                        ? undefined
                        : (ev) => {
                              if (ev.button !== 0) return;
                              ev.stopPropagation();
                              justDragged.current = false;
                              setDrag({ appt: a, originX: ev.clientX, originY: ev.clientY, x: ev.clientX, y: ev.clientY, moved: false });
                          }
                }
                onClick={(ev) => {
                    ev.stopPropagation();
                    if (!inPopover && justDragged.current) { justDragged.current = false; return; }
                    onSelectAppointment(a);
                    if (inPopover) setExpanded(null);
                }}
                /* ⭐ DOUBLE CLICK OPENS THE EDITOR (Pete, 2026-09-27) — see the prop.
                   ⛔ `stopPropagation` or the day CELL beneath takes it, and on this
                   grid that opens the day. ⚠️ It also closes the overflow popover when
                   the chip was inside one: the editor is modal, and leaving a popover
                   open behind it is a second layer nobody can reach past. */
                onDoubleClick={(ev) => {
                    ev.stopPropagation();
                    if (inPopover) setExpanded(null);
                    onEditAppointment?.(a);
                }}
                onContextMenu={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    onRequestContextMenu({ appt: a, x: ev.clientX, y: ev.clientY });
                }}
                onMouseEnter={(ev) => hover.onEnter(ev, a)}
                onMouseLeave={hover.onLeave}
                className={`overflow-hidden rounded px-1 py-px text-[10px] leading-tight ${chipClass(a)} ${isDragging ? 'opacity-40' : ''}`}
                style={{ cursor: inPopover ? 'pointer' : 'grab', touchAction: 'none' }}
                title={
                    fetchEvents
                        ? undefined
                        : a.direction === 'BLOCK'
                          ? a.comments || 'Blocked'
                          : `${a.carrierName || a.carrierCode} → ${a.clientName || a.clientCode}`
                }
            >
                <div className="flex items-center gap-1 truncate">
                    <DirTag dir={a.direction} />
                    {/* ⭐ ONE SLOT, THREE ANSWERS — see the note in GanttCalendar. */}
                    {a.laborActive ? <LaborPulse /> : a.laborPaused ? <LaborPausedMark /> : a.laborDone ? <LaborDoneMark /> : null}
                    <DriverBadge appt={a} />
                    {/* ⭐ Same three marks, same order as the day board. */}
                    <PaperworkBadge appt={a} />
                    <LicenceBadge appt={a} />
                    {/* ⭐ THE "NO WMS" MARK — see the long note in WeekBoard for why it
                        is INLINE here and in a corner on the day board: a month chip is
                        two lines of 10px text in an `overflow-hidden` box with no
                        `relative`, so there is no corner to put it in. Same pill, same
                        footprint as the three badges beside it, and `null` still draws
                        nothing. */}
                    {/* ⭐ 2026-09-28 — the same NO WMS pill as the day board (WmsMarks.tsx). */}
                    {/* ⚠️ 8px, no tracking: THIS board's IN / OUT tag (DirTag), not the day board's. */}
                    {a.direction !== 'BLOCK' && <NoWmsBadge hasWmsOrder={a.hasWmsOrder} className="text-[8px] tracking-normal" />}
                    <span className="whitespace-nowrap font-medium tabular-nums">
                        {format(s, 'h:mmaaa')}–{format(e, 'h:mmaaa')}
                    </span>
                    <span className="truncate">
                        {a.direction === 'BLOCK' ? a.comments || 'Blocked' : a.carrierCode}
                    </span>
                </div>
                <div className="truncate text-[9px] opacity-80">{subLine(a)}</div>
            </div>
        );
    };

    return (
        <div className={`flex h-full flex-col overflow-hidden rounded-lg border ${FRAME_BORDER} bg-card`} style={{ userSelect: drag?.moved ? 'none' : undefined }}>
            {/**
              * ⭐ THE STATUS STRIP, INSIDE THE CARD — exactly where the Gantt puts its
              * own (Pete, 2026-09-19: *"Should be formatted identical"*).
              * ⛔ INSIDE, not above: on the page it was full-viewport-width with no band,
              * which is why the tally sat at the window's edge and an `mb-3` gap opened
              * between it and the grid. `BOARD_HEADER_STRIP_CLASS` is the same string the
              * Gantt and the week board read.
              * ⚠️ It sits ABOVE the day-of-week row, which keeps its own `border-b` — two
              * stacked rules would draw a double line, so the strip supplies the tint and
              * the DOW row supplies the separator it already had.
              */}
            {header && <div className={BOARD_HEADER_STRIP_CLASS}>{header}</div>}
            <div className="grid grid-cols-7 border-b">
                {DOW.map((d) => (
                    <div key={d} className="px-2 py-1.5 text-center text-[11px] uppercase tracking-wide text-muted-foreground">
                        {d}
                    </div>
                ))}
            </div>
            <div className="grid flex-1 grid-cols-7 overflow-auto" style={{ gridAutoRows: 'minmax(6rem, 1fr)' }}>
                {cells.map((c) => {
                    const ds = format(c, 'yyyy-MM-dd');
                    const inMonth = c.getMonth() === monthNum;
                    const list = byDay.get(ds) ?? [];
                    const isToday = ds === todayStr;
                    const bands = (capByDow.get(dowOf(ds)) ?? []).filter((b) => bandAppliesOn(b, ds));
                    return (
                        <div
                            key={ds}
                            data-cell
                            data-date={ds}
                            onClick={() => {
                                if (justDragged.current) { justDragged.current = false; return; }
                                onOpenDay(ds);
                            }}
                            title="Open day view"
                            className={`flex cursor-pointer flex-col gap-0.5 overflow-hidden border-b border-r p-1 align-top transition-colors last:border-r-0 hover:bg-muted/40 ${
                                inMonth ? '' : 'bg-muted/20'
                            }`}
                        >
                            <div className="flex items-center justify-between px-0.5">
                                <span
                                    className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium ${
                                        isToday ? 'bg-primary text-primary-foreground' : inMonth ? '' : 'text-muted-foreground/50'
                                    }`}
                                >
                                    {format(c, 'd')}
                                </span>
                                <div className="flex items-center gap-1">
                                    {bands.length > 0 && (
                                        <span
                                            className="inline-flex items-center gap-0.5"
                                            title={bands
                                                .map((b) => b.maxConcurrent === 0
                                                    ? `Blocked: ${dirShort(b.direction)} ${fmtMin(b.startMinutes)}–${fmtMin(b.endMinutes)} (no appointments)`
                                                    : `Limit: ${dirShort(b.direction)} ${fmtMin(b.startMinutes)}–${fmtMin(b.endMinutes)}, max ${b.maxConcurrent}`)
                                                .join('\n')}
                                        >
                                            {bands.some((b) => b.direction === 'INBOUND' || b.direction === 'BOTH') && <DirBadge k="IN" />}
                                            {bands.some((b) => b.direction === 'OUTBOUND' || b.direction === 'BOTH') && <DirBadge k="OUT" />}
                                        </span>
                                    )}
                                    {list.length > 0 && <span className="text-[10px] text-muted-foreground">{list.length}</span>}
                                </div>
                            </div>

                            <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-hidden">
                                {list.slice(0, MAX_CHIPS).map((a) => renderChip(a))}
                                {list.length > MAX_CHIPS && (
                                    <button
                                        onClick={(ev) => {
                                            ev.stopPropagation();
                                            const cell = (ev.currentTarget as HTMLElement).closest('[data-cell]');
                                            if (cell) setExpanded({ ds, rect: cell.getBoundingClientRect() });
                                        }}
                                        className="px-1 text-left text-[10px] font-medium text-muted-foreground hover:text-foreground"
                                    >
                                        +{list.length - MAX_CHIPS} more
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Drag ghost */}
            {drag?.moved && (
                <div
                    className={`pointer-events-none fixed z-50 rounded px-1 py-px text-[10px] leading-tight shadow-lg ${chipClass(drag.appt)}`}
                    style={{ left: drag.x + 8, top: drag.y + 8, maxWidth: '11rem' }}
                >
                    <span className="font-medium tabular-nums">
                        {format(parseISO(drag.appt.scheduledStartUtc), 'h:mmaaa')}–{format(parseISO(drag.appt.scheduledEndUtc), 'h:mmaaa')}
                    </span>{' '}
                    {drag.appt.direction === 'BLOCK'
                        ? drag.appt.comments || 'Blocked'
                        : drag.appt.carrierName || drag.appt.carrierCode}
                </div>
            )}

            {/* Expanded-day popover — floats over the days below with a highlighted
                background so it reads as the same day expanded; cell size unchanged. */}
            {expanded && (() => {
                const dayList = byDay.get(expanded.ds) ?? [];
                const r = expanded.rect;
                const width = Math.max(r.width, 220);
                const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
                const maxHeight = Math.max(140, window.innerHeight - r.top - 16);
                return (
                    <>
                        <div className="fixed inset-0 z-40" onClick={() => setExpanded(null)} />
                        <div
                            className="fixed z-50 flex flex-col overflow-hidden rounded-md border border-primary/40 bg-accent shadow-xl ring-1 ring-primary/20"
                            style={{ left, top: r.top, width, maxHeight }}
                        >
                            <div className="flex items-center justify-between border-b bg-accent px-2 py-1">
                                <span className="text-xs font-semibold">{format(parseISO(expanded.ds), 'EEE, MMM d')}</span>
                                <button
                                    onClick={() => setExpanded(null)}
                                    className="text-xs leading-none text-muted-foreground hover:text-foreground"
                                    aria-label="Close"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="flex flex-col gap-0.5 overflow-auto p-1">
                                {dayList.map((a) => renderChip(a, true))}
                            </div>
                        </div>
                    </>
                );
            })()}

            {hover.node}
        </div>
    );
}
